import { FC, useState } from 'react';
import { Table } from '@components/common/Table/Table';
import { TableBody } from '@components/common/Table/TableBody/TableBody';
import { TableCell } from '@components/common/Table/TableCell/TableCell';
import { TableContainer } from '@components/common/Table/TableContainer/TableContainer';
import { TableHead } from '@components/common/Table/TableHead/TableHead';
import { TableRow } from '@components/common/Table/TableRow/TableRow';
import { TableSortLabel } from '@components/common/Table/TableSortLabel/TableSortLabel';
import {
  IApplicationsAnalystTableProps,
  tableColumnsNames,
  ApplicationForListingDto,
  ApplicationListingRowFormatted,
} from './types';
import { FormattedMessage } from 'react-intl';
import { SortDirectionEnum } from '@legacy/shared-types/enums/sort-direction.enum';
import { useApplications } from '@queries/applications/useApplications';
import { getPageURL, PageURL } from '@router/pageURL';
import { useNavigate } from 'react-router-dom';
import { EditIcon } from '@components/common/Icons/EditIcon';
import { FormattedTableCell, isLegacyUuidApplicationId } from './FormattedTableCell';
import { useQueryClient } from '@tanstack/react-query';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { parseApplicationRoutesAndSaveToStorage } from '@utils/localStorage';
import { allCapsToTitleCase } from '@common/helpers';

export const ApplicationsTable: FC<IApplicationsAnalystTableProps> = ({ searchInputValue }) => {
  const [order, setOrder] = useState<SortDirectionEnum>(SortDirectionEnum.DESC);
  const [orderBy, setOrderBy] = useState<keyof ApplicationForListingDto>('submittedAt');
  const { data: allApplications, isLoading } = useApplications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const formattedApplications: ApplicationListingRowFormatted[] | undefined = allApplications?.map(
    (application: ApplicationForListingDto) => {
      const isLegacyUuid = isLegacyUuidApplicationId(application.id);

      return {
        id: isLegacyUuid ? application.id : application.referenceNumber || application.id,
        caseId: application.id,
        applicant: application.applicant,
        coApplicant: application.coApplicant,
        status: <FormattedMessage id={`applicationStatus.${application.status}`} />,
        type: allCapsToTitleCase(application.type),
        createdAt: application.createdAt || application.submittedAt,
        submittedAt: application.submittedAt,
      };
    },
  );

  const sortedRows = formattedApplications?.sort((a: any, b: any) => {
    if (a[orderBy] < b[orderBy]) {
      return order === SortDirectionEnum.ASC ? -1 : 1;
    }

    if (a[orderBy] > b[orderBy]) {
      return order === SortDirectionEnum.ASC ? 1 : -1;
    }
    return 0;
  });

  const filteredBySearchInputValue = sortedRows?.filter((row: ApplicationListingRowFormatted) => {
    const rowValues = Object.values(row);
    return rowValues.some((value: any) => value?.toString()?.toLowerCase().includes(searchInputValue?.toLowerCase()));
  });

  const sortHandler = (column: keyof ApplicationForListingDto) => {
    const isAsc = order === SortDirectionEnum.ASC;
    const isSameColumn = orderBy === column;

    setOrder(isAsc && isSameColumn ? SortDirectionEnum.DESC : SortDirectionEnum.ASC);
    setOrderBy(column);
  };

  const applicationdDetailsPreloadHandler = async (applicationId: string | number) => {
    await queryClient.prefetchQuery(['application-in-progress', applicationId], () =>
      applicationFormApi.getApplicationInProgress(String(applicationId)),
    );
    const applicationInProgress = queryClient.getQueryData<Record<string, any>>([
      'application-in-progress',
      applicationId,
    ]);

    if (applicationInProgress) parseApplicationRoutesAndSaveToStorage(applicationInProgress);

    navigate(getPageURL(PageURL.APPLICATION_EDIT, { id: applicationId }));
  };

  return (
    <div className={'mb-2 w-full overflow-hidden rounded-sm bg-white shadow-sm'}>
      <TableContainer>
        <Table aria-label={'collapsible table'}>
          <TableHead>
            <TableRow>
              <TableCell />
              {tableColumnsNames.map(columnName => (
                <TableCell key={columnName}>
                  <TableSortLabel
                    data-value={columnName}
                    active={orderBy === columnName}
                    direction={order}
                    onClick={() => sortHandler(columnName as keyof ApplicationForListingDto)}
                  >
                    <FormattedMessage id={`application.${columnName}`} />
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody isLoading={isLoading} columnsNumber={tableColumnsNames.length}>
            {filteredBySearchInputValue?.length ? (
              filteredBySearchInputValue.map((row: ApplicationListingRowFormatted, index: number) => (
                <TableRow key={index}>
                  <TableCell />
                  {Object.entries(row)
                    .filter(([objectKey]) => objectKey !== 'caseId')
                    .map(([objectKey, value]: [string, any]) => (
                      <TableCell key={objectKey}>
                        <FormattedTableCell objectKey={objectKey} value={value} caseId={row.caseId} />
                      </TableCell>
                    ))}
                  <TableCell>
                    {!row.submittedAt && isLegacyUuidApplicationId(row.caseId) && (
                      <button
                        type={'button'}
                        className={'inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0'}
                        onClick={() => applicationdDetailsPreloadHandler(row.caseId)}
                      >
                        <EditIcon htmlColor={'grey'} />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className={'text-center'} colSpan={9}>
                  <FormattedMessage id={'common.itemsNotFound'} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
